import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./AdCampaignLocationsPage.scss";
import api from "../api/axios";
import { buildAdvertiserPublicUrl, copyTextToClipboard } from "../utils/publicLinks";

export default function AdCampaignLocationsPage() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [campaign, setCampaign] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [placementIdsSelected, setPlacementIdsSelected] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [clientLinkLoading, setClientLinkLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    useEffect(() => {
        if (!id) {
            return;
        }

        setLoading(true);
        setError("");
        setSuccess("");

        api.get(`/admin/ad-campaigns/${id}/locations`)
            .then(({ data }) => {
                setCampaign(data.campaign || null);
                setCompanies(data.companies || []);
                setPlacementIdsSelected([]);
            })
            .catch(() => {
                setError("Не удалось загрузить точки кампании.");
                setCampaign(null);
                setCompanies([]);
            })
            .finally(() => setLoading(false));
    }, [id]);

    const allVisiblePlacementIds = useMemo(
        () =>
            companies.flatMap((company) =>
                (company.addresses || []).map((addr) => addr.placementId)
            ),
        [companies]
    );

    const isAllSelected = useMemo(() => {
        if (allVisiblePlacementIds.length === 0) {
            return false;
        }

        return allVisiblePlacementIds.every((pid) =>
            placementIdsSelected.includes(pid)
        );
    }, [allVisiblePlacementIds, placementIdsSelected]);

    const selectedCount = placementIdsSelected.length;

    const togglePlacement = (placementId) => {
        setSuccess("");
        setError("");

        setPlacementIdsSelected((prev) =>
            prev.includes(placementId)
                ? prev.filter((itemId) => itemId !== placementId)
                : [...prev, placementId]
        );
    };

    const toggleAllForCompany = (companyId) => {
        setSuccess("");
        setError("");

        const company = companies.find((item) => item.id === companyId);
        if (!company) {
            return;
        }

        const ids = (company.addresses || []).map((addr) => addr.placementId);
        const allSelectedForCompany = ids.every((itemId) =>
            placementIdsSelected.includes(itemId)
        );

        setPlacementIdsSelected((prev) => {
            if (allSelectedForCompany) {
                return prev.filter((itemId) => !ids.includes(itemId));
            }

            const next = new Set(prev);
            ids.forEach((itemId) => next.add(itemId));
            return Array.from(next);
        });
    };

    const toggleSelectAll = () => {
        setSuccess("");
        setError("");

        if (isAllSelected) {
            setPlacementIdsSelected([]);
            return;
        }

        setPlacementIdsSelected(allVisiblePlacementIds);
    };

    const handleSendNotifications = () => {
        if (!id || placementIdsSelected.length === 0) {
            return;
        }

        setSending(true);
        setError("");
        setSuccess("");

        api.post(`/admin/ad-campaigns/${id}/notify`, {
            placementIds: placementIdsSelected,
        })
            .then(({ data }) => {
                setSuccess(data.message || "Уведомления отправлены.");
            })
            .catch((err) => {
                const message =
                    err?.response?.data?.error ||
                    "Ошибка отправки уведомлений.";
                setError(message);
            })
            .finally(() => setSending(false));
    };

    const handleBack = () => {
        navigate("/ad-campaigns");
    };

    const handleCopyClientLink = async () => {
        if (!campaign?.id) {
            return;
        }

        setClientLinkLoading(true);
        setError("");
        setSuccess("");

        try {
            const { data } = await api.get(`/advertisers/generate/${campaign.id}`);
            const publicUrl = buildAdvertiserPublicUrl(data?.hash, data?.url);

            await copyTextToClipboard(publicUrl);
            setSuccess("Ссылка для клиента скопирована.");
        } catch (err) {
            setError(
                err?.response?.data?.error ||
                err?.message ||
                "Не удалось сформировать ссылку клиента."
            );
        } finally {
            setClientLinkLoading(false);
        }
    };

    const handleCopyPartnerLink = async (url) => {
        setError("");
        setSuccess("");

        try {
            await copyTextToClipboard(url);
            setSuccess("Ссылка для партнёра скопирована.");
        } catch (err) {
            setError(err?.message || "Не удалось скопировать ссылку партнёра.");
        }
    };

    return (
        <div className="ad-campaign-locations-page">
            <div className="ad-campaign-locations-header">
                <div className="ad-campaign-locations-header-main">
                    <button
                        type="button"
                        className="ad-campaign-back-button"
                        onClick={handleBack}
                    >
                        ← Назад
                    </button>
                    <div>
                        <h2>
                            Точки рекламной кампании
                            {campaign ? `: ${campaign.title}` : ""}
                        </h2>
                        {campaign && (
                            <div className="ad-campaign-locations-subtitle">
                                Ролик id={campaign.id}
                            </div>
                        )}
                    </div>
                </div>

                <div className="ad-campaign-locations-actions">
                    <div className="ad-campaign-locations-selection-info">
                        Выбрано точек: {selectedCount}
                    </div>
                    <button
                        type="button"
                        className="ad-campaign-toggle-all-button"
                        onClick={toggleSelectAll}
                        disabled={allVisiblePlacementIds.length === 0}
                    >
                        {isAllSelected ? "Снять все галочки" : "Выбрать все"}
                    </button>
                    <button
                        type="button"
                        className="ad-campaign-client-link-button"
                        onClick={handleCopyClientLink}
                        disabled={clientLinkLoading || loading || !campaign?.id}
                    >
                        {clientLinkLoading ? "Формируем..." : "Ссылка клиенту"}
                    </button>
                    <button
                        type="button"
                        className="ad-campaign-send-button"
                        onClick={handleSendNotifications}
                        disabled={
                            sending ||
                            placementIdsSelected.length === 0 ||
                            loading
                        }
                    >
                        {sending ? "Отправка..." : "Отправить уведомления"}
                    </button>
                </div>
            </div>

            {loading && (
                <div className="ad-campaign-locations-loading">
                    Загрузка точек...
                </div>
            )}

            {error && !loading && (
                <div className="ad-campaign-locations-error">
                    {error}
                </div>
            )}

            {success && !loading && (
                <div className="ad-campaign-locations-success">
                    {success}
                </div>
            )}

            {!loading && !error && companies.length === 0 && (
                <div className="ad-campaign-locations-empty">
                    Нет точек для этой кампании. Возможно, размещений еще нет или по всем размещениям уже есть фотоотчеты.
                </div>
            )}

            {!loading && !error && companies.length > 0 && (
                <div className="ad-campaign-locations-content">
                    {companies.map((company) => {
                        const companyPlacementIds = (company.addresses || []).map(
                            (addr) => addr.placementId
                        );
                        const companyAllSelected =
                            companyPlacementIds.length > 0 &&
                            companyPlacementIds.every((itemId) =>
                                placementIdsSelected.includes(itemId)
                            );

                        return (
                            <div
                                className="ad-campaign-company-block"
                                key={company.id}
                            >
                                <div className="ad-campaign-company-header">
                                    <label className="ad-campaign-company-select">
                                        <input
                                            type="checkbox"
                                            checked={companyAllSelected}
                                            onChange={() =>
                                                toggleAllForCompany(company.id)
                                            }
                                        />
                                        <span className="label-text">
                                            {company.name}
                                        </span>
                                    </label>
                                </div>

                                <div className="ad-campaign-locations-table-wrapper">
                                    <table className="ad-campaign-locations-table">
                                        <thead>
                                            <tr>
                                                <th className="col-checkbox">✓</th>
                                                <th>Адрес</th>
                                                <th>Ссылка партнёра</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {company.addresses &&
                                            company.addresses.length > 0 ? (
                                                company.addresses.map((addr) => (
                                                    <tr key={addr.placementId}>
                                                        <td
                                                            className="col-checkbox"
                                                            data-label="Выбрать"
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={placementIdsSelected.includes(
                                                                    addr.placementId
                                                                )}
                                                                onChange={() =>
                                                                    togglePlacement(
                                                                        addr.placementId
                                                                    )
                                                                }
                                                            />
                                                        </td>
                                                        <td data-label="Адрес">
                                                            {addr.address}
                                                        </td>
                                                        <td data-label="Ссылка">
                                                            <div className="ad-campaign-link-actions">
                                                                <a
                                                                    href={addr.url}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                >
                                                                    Открыть
                                                                </a>
                                                                <button
                                                                    type="button"
                                                                    className="ad-campaign-link-copy-button"
                                                                    onClick={() =>
                                                                        handleCopyPartnerLink(
                                                                            addr.url
                                                                        )
                                                                    }
                                                                >
                                                                    Скопировать
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td
                                                        colSpan={3}
                                                        className="ad-campaign-locations-no-addresses"
                                                    >
                                                        Нет активных адресов для рассылки
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
